import { Fragment, lazy, ReactNode, Suspense, useCallback } from "react";
import { IGif, INicheMap, IUser } from "services/types";

import { FeedAds } from "components/ads/FeedAds";
import { MobileContentPageScrollKeys } from "components/ads/config/units/contentPageScrollMobile";
import { DesktopContentPageScrollKeys } from "components/ads/config/units/contentPageScrollDesktop";
import { FeedControl } from "components/feed/FeedControl";
import { TileFeed } from "./TileFeed";
import { ViewRegion } from "utils/metrics";
import { Loader } from "components/UI/Loader";

import "./GifList.scss";
import { PreviewFeed } from "./PreviewFeed";

const InjectionFeed = lazy(() => import("./InjectionFeed"));

export interface IUserList {
  [key: string]: IUser;
}

interface IProps {
  ordering?: boolean;
  orderingTop?: boolean;
  topics?: boolean;
  manage?: boolean;

  topic: string;
  order: string;
  tiles: boolean;
  verified?: boolean;
  gifs: IGif[];
  users: IUserList;
  niches?: INicheMap;
  totalLength: number;
  hasMore: boolean;
  changeTopic: (topic: string) => void;
  changeOrder: (order: string) => void;
  changeMode: (tiles: boolean) => void;
  changeVerified?: (verified: boolean) => void;
  fetchMore: () => void;
  userID?: string;
  isError?: boolean;
  isLoading?: boolean;
  className?: string;
  injector?: (pos: number) => JSX.Element;
  onSelect?: (ids: string[]) => void;
  source: ViewRegion;
  sourceId: string | null;
  isVerifyHidden?: boolean;
  showPinnedGifs?: boolean;
  // todo: remove later (when all tiles will be replaced)
  isVerticalTiles?: boolean;
  currentTags?: string[];

  // Passed to the watch page to know where the user comes from.
  rel?: string;
}

export const GifList = (props: IProps) => {
  const { injector: parentInjector } = props;

  const injector = useCallback((pos: number): JSX.Element => {
    const children: ReactNode[] = [];

    if (parentInjector) {
      return parentInjector(pos);
    }

    children.push(
      <FeedAds
        key={`feedAds-${pos}`}
        position={pos}
        mobileAds={MobileContentPageScrollKeys}
        desktopAds={DesktopContentPageScrollKeys}
      />
    );

    return <Fragment>{children}</Fragment>;
  }, [parentInjector]);

  return (
    <div className={`gifList ${props.className ? props.className : ""}`}>
      <FeedControl
        order={props.order}
        ordering={props.ordering}
        orderingTop={props.orderingTop}
        tiles={props.tiles}
        verified={!!props.verified}
        topics={props.topics}
        topic={props.topic}
        changeOrder={props.changeOrder}
        changeMode={props.changeMode}
        changeVerified={props.changeVerified}
        changeTopic={props.changeTopic}
      />

      {props.isError && <p className="errorMessage">Error loading gifs, please refresh.</p>}

      {props.tiles && <TileFeed
        gifs={props.gifs}
        hasMore={props.hasMore}
        fetchMore={props.fetchMore}
        manage={!!props.manage}
        onSelect={props.onSelect}
        injector={injector}
        source={props.source}
        rel={props.rel}
        order={props.order}
        verified={!!props.verified}
        isLoading={props.isLoading}
        isVerifyHidden={props.isVerifyHidden}
        showPinnedGifs={props.showPinnedGifs}
        userId={props.userID}
        isVerticalTiles={props.isVerticalTiles}
      />}

      {!props.tiles && (
        <PreviewFeed>
          <Suspense fallback={<Loader />}>
            <InjectionFeed
              gifs={props.gifs}
              users={props.users}
              niches={props.niches}
              source={props.source}
              sourceId={props.sourceId}
              injector={injector}
              rel={props.rel}
              isLoading={props.isLoading}
              fetchMore={props.fetchMore}
              hasMore={props.hasMore}
            />
          </Suspense>
        </PreviewFeed>
      )}
    </div>
  );
};
